import { useCallback, useEffect, useState } from 'react'
import {
  showSuccess,
  showValidationMessage,
  formatApiError,
} from '@/lib/utils/notifications'
import type { UpdateUserRequest, UseUserManagementEditFormParams } from '../types'
import {
  cleanPhone,
  formatUserEmail,
} from '../utils/userManagementFormatters'
import {
  validateName,
  validateMobile,
  isEmailNotLinked,
} from '../utils/userManagementValidators'
import { updateUser } from '../services/userManagementApi'
import { trackApplicationEvent } from '@/lib/utils/analytics'

export function useUserManagementEditForm({
  userId,
  user,
  fetchUser,
  patchUser,
}: UseUserManagementEditFormParams) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [, setLastNameTouched] = useState(false)
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [refundActionNotif, setRefundActionNotif] = useState(false)
  const [refundActionEmail, setRefundActionEmail] = useState(false)
  const [invoiceActionNotif, setInvoiceActionNotif] = useState(false)
  const [invoiceActionEmail, setInvoiceActionEmail] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [rejectedEmails, setRejectedEmails] = useState<string[]>([])

  useEffect(() => {
    if (!userId) return
    void fetchUser().then((data) => {
      if (!data) return
      setFirstName(data.first_name ?? '')
      setLastName(data.last_name ?? '')
      setPhone(cleanPhone(data.mobile_no ?? ''))
      setEmail(formatUserEmail(data.email) ?? '')
      setJobTitle(data.job_title ?? '')
      if (data.actions) {
        setRefundActionNotif(data.actions.refund_contract_approval?.action_notification ?? false)
        setRefundActionEmail(data.actions.refund_contract_approval?.action_email ?? false)
        setInvoiceActionNotif(data.actions.invoice_approval?.action_notification ?? false)
        setInvoiceActionEmail(data.actions.invoice_approval?.action_email ?? false)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const getLastNameError = useCallback((val: string): string => {
    if (!val) return 'Last Name is required.'
    if (!validateName(val)) return 'Enter Valid Last Name'
    return ''
  }, [])

  const getFirstNameError = useCallback((val: string): string => {
    if (!val) return 'First Name is required.'
    if (!validateName(val)) return 'Enter Valid First Name'
    return ''
  }, [])

  const getPhoneError = useCallback((val: string): string => {
    const v = val.trim()
    if (!v) return 'Contact Phone is required.'
    if (!validateMobile(v)) return 'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
    return ''
  }, [])

  const validateForm = (): boolean => {
    const err: Record<string, string> = {}
    const fn = getFirstNameError(firstName)
    if (fn) err.first_name = fn
    const ln = getLastNameError(lastName)
    if (ln) err.last_name = ln
    const ph = getPhoneError(phone)
    if (ph) err.phone = ph
    if (email.trim() && !isEmailNotLinked(email.trim(), rejectedEmails)) {
      err.email = 'An account is already linked to this email address. Please change email.'
    }
    setFormErrors(err)
    return Object.keys(err).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm() || !userId) {
      setTimeout(() => {
        const firstErrEl = document.querySelector<HTMLElement>(
          'input.border-rose-500, [data-testid^="um-edit-"].border-rose-500'
        )
        if (firstErrEl) {
          firstErrEl.focus()
          firstErrEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 0)
      return
    }
    setSubmitting(true)
    try {
      const payload: UpdateUserRequest = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile_no: cleanPhone(phone),
        job_title: jobTitle.trim() || undefined,
        actions: {
          refund_contract_approval: {
            action_notification: refundActionNotif,
            action_email: refundActionEmail,
          },
          invoice_approval: {
            action_notification: invoiceActionNotif,
            action_email: invoiceActionEmail,
          },
        },
      }
      if (email.trim()) payload.email = email.trim()
      await updateUser(userId, payload)
      trackApplicationEvent('user_updated', 'user_management')
      showSuccess('User Edited Successfully!')
      patchUser({
        first_name: payload.first_name,
        last_name: payload.last_name,
        mobile_no: payload.mobile_no,
        email: payload.email ?? user?.email ?? '',
        job_title: payload.job_title ?? '',
        actions: payload.actions,
      })
      void fetchUser({ silent: true })
    } catch (err) {
      const raw = (err as { error?: unknown }).error ?? err
      const msg = typeof raw === 'string' ? raw : formatApiError(err)
      if (typeof msg === 'string' && msg.includes('already linked')) {
        setRejectedEmails((prev) => [...new Set([...prev, email.trim().toLowerCase()])])
        setFormErrors((prev) => ({
          ...prev,
          email: 'An account is already linked to this email address. Please change email.',
        }))
      }
      showValidationMessage(msg, 'Validation Message')
    } finally {
      setSubmitting(false)
    }
  }

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    setLastNameTouched,
    phone,
    setPhone,
    email,
    setEmail,
    jobTitle,
    setJobTitle,
    refundActionNotif,
    setRefundActionNotif,
    refundActionEmail,
    setRefundActionEmail,
    invoiceActionNotif,
    setInvoiceActionNotif,
    invoiceActionEmail,
    setInvoiceActionEmail,
    formErrors,
    setFormErrors,
    submitting,
    getFirstNameError,
    getLastNameError,
    getPhoneError,
    handleSubmit,
  }
}
